/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:35:58AM]
Permissions to update outside the main branch: [true]
*/

import {
    Button,
    DocumentTitle,
    Form,
    requiredRule,
    useCondition,
    ValidatedInput
} from '@finbeat/react-toolkit';
import { login } from 'axios/accountsApi';
import Window from 'components/Window';
import useApi from 'hooks/useApi';
import useAuth, { initialAuth } from 'hooks/useAuth';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Card, CardBody, Col, FormGroup, Row } from 'reactstrap';
import errorToText from 'utils/errorToText';

export type LoginForm = {
    username: string;
    password: string;
};

const Login = () => {
    const { setAuth } = useAuth();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const { designType } = useGeneralSettingsContext();

    const userRef = useRef<HTMLInputElement>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const redirectUri = searchParams.get('redirectUri');
    const from = (location.state?.from?.pathname as string) || '/';

    const api = useApi();
    const loginMutation = useMutation(
        (data: LoginForm) => login(api, data.username, data.password),
        {
            onSuccess: (data) => {
                const userInfo = data || initialAuth;
                setAuth({
                    user: userInfo.userName,
                    userFullName: `${userInfo.lastName} ${userInfo.firstName} ${userInfo.middleName}`,
                    userId: userInfo.userId,
                    roles: userInfo.roles,
                    apiCrmToken: userInfo.apiCrmToken,
                    accessTokenExpires: userInfo.tokenExpires,
                    accessTokenValid: true,
                    tariffEditingAccess: userInfo.tariffEditingAccess,
                    tariffEditableBillServices: userInfo.tariffEditableBillServices,
                    canViewOrdersWithActiveProlongationAgreements:
                        userInfo.canViewOrdersWithActiveProlongationAgreements,
                    portalUserEmployerId: userInfo.portalUserEmployerId,
                    showMerchantInstruction: userInfo.showMerchantInstruction,
                    userStartPage: userInfo.userStartPage,
                });
                if (redirectUri) {
                    window.location.href = redirectUri;
                } else {
                    userInfo.userStartPage?.length > 0
                        ? navigate(userInfo.userStartPage[0], { replace: true, relative: 'path' })
                        : navigate(from, { replace: true });
                }
            },
        }
    );

    const onSubmit = (data: LoginForm) => {
        loginMutation.mutate(data);
    };

    useEffect(() => {
        const current = userRef?.current as any;
        if (current && current instanceof HTMLInputElement) {
            current.focus();
        }
    }, []);

    return useCondition(
        designType,
        {
            nova: (
                <Window<LoginForm>
                    onSubmit={(data) => onSubmit(data)}
                    label={t('BackOfficeLoginPageName')}
                >
                    <ValidatedInput<LoginForm>
                        data-testid="input-username"
                        id="username"
                        name="username"
                        placeholder={t('BackOfficeLogin')}
                        type="text"
                        innerRef={userRef}
                        disabled={loginMutation.isLoading}
                        rules={{ required: requiredRule }}
                        label={t('BackOfficeLogin')}
                    />
                    <ValidatedInput<LoginForm>
                        data-testid="input-password"
                        id="password"
                        name="password"
                        placeholder={t('BackOfficePassword')}
                        type="password"
                        disabled={loginMutation.isLoading}
                        rules={{ required: requiredRule }}
                        label={t('BackOfficePassword')}
                    />

                    {loginMutation.isError && (
                        <Alert color="danger" data-testid="alert-error">
                            {errorToText(loginMutation.error as any, t)}
                        </Alert>
                    )}
                    <Button
                        type="submit"
                        color="primary"
                        isPending={loginMutation.isLoading}
                        data-testid="login-button"
                    >
                        <span>
                            <Trans i18nKey="BackOfficeLoginPageName" />
                        </span>
                    </Button>
                    <div className="flex-centered">
                        <button
                            className="window__link-text-btn btn-container cursor--pointer"
                            onClick={() => navigate('/forgot-password')}
                            color="primary"
                        >
                            <Trans i18nKey="BackOfficeForgotPassword" />
                        </button>
                    </div>
                </Window>
            ),
        },
        <DocumentTitle code="BackOfficeLoginPageName">
            <div className="AuthForm">
                <Row>
                    <Col sm="3" />
                    <Col sm="6">
                        <Card>
                            <CardBody>
                                <Form<LoginForm>
                                    clearAfterSubmit={false}
                                    onSubmit={(data) => onSubmit(data)}
                                >
                                    <FormGroup>
                                        <ValidatedInput<LoginForm>
                                            data-testid="input-username"
                                            id="username"
                                            name="username"
                                            placeholder={t('BackOfficeLogin')}
                                            type="text"
                                            innerRef={userRef}
                                            disabled={loginMutation.isLoading}
                                            rules={{ required: requiredRule }}
                                            label={t('BackOfficeLogin')}
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <ValidatedInput<LoginForm>
                                            data-testid="input-password"
                                            id="password"
                                            name="password"
                                            placeholder={t('BackOfficePassword')}
                                            type="password"
                                            disabled={loginMutation.isLoading}
                                            rules={{ required: requiredRule }}
                                            label={t('BackOfficePassword')}
                                        />
                                    </FormGroup>
                                    {loginMutation.isError && (
                                        <Alert color="danger" data-testid="alert-error">
                                            {errorToText(loginMutation.error as any, t)}
                                        </Alert>
                                    )}
                                    <div className="d-flex justify-content-between">
                                        <Button
                                            type="submit"
                                            isPending={loginMutation.isLoading}
                                            data-testid="login-button"
                                        >
                                            <span>
                                                <Trans i18nKey="BackOfficeLoginPageName" />
                                            </span>
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={() => navigate('/forgot-password')}
                                        >
                                            <Trans i18nKey="BackOfficeForgotPassword" />
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </DocumentTitle>
    );
};

export default Login;
